/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { BuildingOffice2Icon, UserCircleIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Flex,
  Box
} from "@mantine/core";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  queryClient, useAllAutomations, useAllPlaybooks,
  useWorkspaceObjectSchema
} from "../../../../hooks/QueryHooks";
import { updateAutomation } from "../../../../services/automation/automationEndpoints";
import { ENTITY } from "../../../../services/workspace/workspaceEndpoints.enums";
import MButton from "../../../../shared/ui/buttons/MButton";
import useSignalStore from "../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import useHeaderStore from "../../../../store/layout/headerStore";
import { getLargerTimeWindow } from "../../../../utils/dateConverter";
import notify from "../../../../utils/notify";
import NewSignalBody from "./components/NewSignalBody";
import { getDefaultSortField } from "../../../../shared/filter/utils/entityDisplayUtils";

// typeChoices is now dynamic — see NewSignalBody entityOptions

const basedOnChoices = [
  { value: "usage_based", label: "Usage - hits a usage threshold" },
  { value: "event_based", label: "Event - does a specific action" },
];

const EditSignalPage = () => {
  const {
    signal,
    setSignal,
    setName,
    setPlaybook,
    setEntityRelation,
    setTrigger,
    setMetrics,
    setSelectedFields,
    addFilter,
    addPredicate,
    editPredicate,
    deletePredicate,
    deleteFilter,
    clearFilter,
    clearSignal,
    setTimeWindow,
    setSuppressionPeriod,
    setModelKey,
    createAction,
    deleteAction,
    updateAction,
    setResponseData,
  } = useSignalStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { setHeaderContent, clearHeader, resetHeader } = useHeaderStore();
  const params = useParams();

  // Fetch both signals and automations
  const { data: signals, isSuccess: isSignalsSuccess }: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const { data: automations, isSuccess: isAutomationsSuccess }: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "automation",
    enabled: !!workspace?.id,
  });

  const [error, setError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [isAutomation, setIsAutomation] = useState<boolean>(false);

  useEffect(() => {
    if (params?.signalId && (isSignalsSuccess || isAutomationsSuccess)) {
      // Try to find in signals first
      let selectedSignal = signals?.find((sig: any) => sig.id === params?.signalId);
      let isAuto = false;

      // If not found, try automations
      if (!selectedSignal) {
        selectedSignal = automations?.find((sig: any) => sig.id === params?.signalId);
        if (selectedSignal) {
          isAuto = true;
        }
      }
      setIsAutomation(isAuto);

      if (selectedSignal) {
        // Ensure sort object exists with proper defaults
        const defaultSort = {
          field: getDefaultSortField(selectedSignal.entity_relation || "accounts"),
          order: "desc",
          type: "property",
        };

        const existingSort = selectedSignal.sort || defaultSort;

        const signalWithSort = {
          ...selectedSignal,
          sort: existingSort,
          metrics: [],
          // For automations, ensure frequency is set to all_time
          automation_actions: isAuto ? {
            ...selectedSignal.automation_actions,
            suppression_period: "all_time"
          } : selectedSignal.automation_actions
        };

        setSignal(signalWithSort);
      } else {
        console.warn("EditSignalPage: No signal found with ID:", params?.signalId);
      }
    }
  }, [params?.signalId, isSignalsSuccess, isAutomationsSuccess, signals, automations]);

  const workspaceSchema: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: playBookData }: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const playbooks = useMemo(() => playBookData?.map((playbook: any) => ({
    label: playbook?.title,
    value: playbook?.id,
  })), [playBookData?.length]);

  const updateSignalMutation = useMutation({
    mutationFn: updateAutomation,
  });

  const handleFilterAdd = () => {
    const selectedFilters = signal
      .filters.predicates[signal.filters.predicates.length - 1]?.predicates || [];
    if (selectedFilters.length === 0 || selectedFilters[0].field !== "") {
      addFilter({
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        }],
      });
    }
  };

  const handlePredicateAdd = (filterId?: any) => {
    const selectedFilters = signal.filters.predicates[filterId].predicates;
    if (selectedFilters.length === 0 || selectedFilters[selectedFilters.length - 1].field !== "") {
      addPredicate(
        {
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        },
        filterId,
      );
    }
  };

  const handlePredicateClear = (filterId?: any) => {
    deleteFilter(filterId);
  };

  const handlePredicateChange = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entity_type?: string,
    id?: any,
    filterId?: any,
    time_window?: string,
  ) => {
    editPredicate({
      field,
      operator,
      value,
      type,
      entity_type: entity_type || "column",
      time_window,
    }, id, filterId);
    const selectedColumns = signal?.selected_fields || [];
    let newColumns: any = selectedColumns;
    if (field.startsWith(`${signal?.entity_relation}.`)) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    }
    setMetrics([]);
    setTimeWindow(getLargerTimeWindow(signal?.time_window, time_window));
  };

  const handlePredicateRemove = (id: number, filterId?: any) => {
    deletePredicate(id, filterId);
  };

  const navigate = useNavigate();

  const onTimeWindowChange = (value: any) => {
    setTimeWindow(value);
  };

  const handleEntityChange = (value: string) => {
    if (value === "accounts" && signal.entity_relation !== "accounts") {
      setSelectedFields(["accounts.name", "accounts.last_seen", "accounts.created_at"]);
    } else if (value === "users" && signal.entity_relation !== "users") {
      setSelectedFields(["users.name", "users.first_name", "users.last_seen", "users.created_at"]);
    }
    setEntityRelation(value);
  };

  console.log('currentSignal', signal);

  const onSignalSubmit = () => {
    // Get the latest signal value from the store to avoid stale closure issues
    const currentSignal = useSignalStore.getState().signal;

    const slackEnabled = currentSignal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_slack");
    const emailEnabled = currentSignal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_email");
    const inboxEnabled = currentSignal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_inbox");
    if (!currentSignal?.name || !currentSignal?.entity_relation || !currentSignal?.trigger
      || !currentSignal?.filters?.predicates[0]?.predicates?.length
      || !currentSignal?.automation_actions?.actions?.length
      || !currentSignal?.automation_actions?.suppression_period
      || !currentSignal?.time_window
      || (!!slackEnabled && ((slackEnabled?.message_type === "channel" && !slackEnabled?.channel_id)
        || (slackEnabled?.message_type === "direct" && !slackEnabled?.slack_user_id)))
      || (!!emailEnabled && (!emailEnabled?.to || !emailEnabled?.subject))) {
      setError(true);
      notify({
        messageList: ["Kindly resolve errors in form fields"],
        type: "warning",
      });
    } else {
      // Fix sort before submission
      const sortConfig = {
        field: currentSignal.sort?.field || "",
        order: currentSignal.sort?.order || "desc",
        type: currentSignal.sort?.type || "property",
      };

      // If sort field is empty or invalid, set default based on entity type
      if (!sortConfig.field || sortConfig.field === "") {
        sortConfig.field = getDefaultSortField(currentSignal.entity_relation || "accounts");
        sortConfig.order = "desc";
        sortConfig.type = "property";
      }

      // Ensure selected_fields is not empty - use empty array as default
      const selectedFields = currentSignal.selected_fields && currentSignal.selected_fields.length > 0
        ? currentSignal.selected_fields
        : [];

      // Ensure metrics is not empty - use default metric based on entity type
      const metrics: string[] = [];

      const updatedSignal: any = currentSignal;
      delete (updatedSignal?.automation_actions?.run_schedule);

      updateSignalMutation.mutate({
        ...updatedSignal,
        selected_fields: selectedFields,
        metrics,
        sort: sortConfig,
        automation_id: params?.signalId || "",
        time_zone: workspace?.workspace_time_zone,
        status: "active",
        workspace_id: workspace?.id,
      }, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["all_automations", workspace?.id, currentSignal?.automation_type], (oldData: any) => ({
            ...oldData,
            data: oldData?.data?.map(
              (item: any) => (item?.id === data?.data?.id ? data?.data : item),
            ),
          }));
          if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "signal_updated",
              {
                properties: {
                  name: currentSignal?.name,
                  entity_relation: currentSignal?.entity_relation,
                  automation_id: params?.signalId || "",
                  trigger: currentSignal?.trigger,
                  actions: currentSignal?.automation_actions?.actions?.map((action: any) => action?.type),
                  time_zone: workspace?.workspace_time_zone,
                  status: "active",
                  workspace_id: workspace?.id,
                },
              },
            );
          }
          notify({
            type: "success",
            messageList: ["Signal updated successfully."],
          });
          navigate(-1);
        },
        onError: (err: any) => {
          if (err.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while updating the signal. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  // Cleanup on unmount only - moved to return statement in header useEffect
  useEffect(
    () => () => {
      clearSignal();
      resetHeader();
    },
    [],
  );

  // Header Actions Integration
  useEffect(() => {
    setHeaderContent({
      title: isAutomation ? "Edit Automation" : "Edit Signal",
      description: isAutomation ? "Update your automation configuration" : "Update your signal configuration",
      actions: (
        <Flex gap="md" align="center">
          <Flex gap="xs">
            <Button
              variant="default"
              size="xs"
              onClick={() => {
                clearSignal();
                navigate(-1);
              }}
              disabled={updateSignalMutation.isLoading}
              leftSection={<IconX size={14} />}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={onSignalSubmit}
              loading={updateSignalMutation.isLoading}
              leftSection={<IconDeviceFloppy size={14} />}
            >
              {updateSignalMutation.isLoading ? 'Updating...' : (isAutomation ? 'Update Automation' : 'Update Signal')}
            </Button>
          </Flex>
        </Flex>
      )
    });
  }, [updateSignalMutation.isLoading, isAutomation]);

  return (
    <Box className="h-[calc(100vh-100px)] flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto">
        <NewSignalBody
          setSelectedFields={setSelectedFields}
          setSelectedMetrics={setMetrics}
          clearFilter={clearFilter}
          clearSignal={clearSignal}
          onTimeWindowChange={onTimeWindowChange}
          handlePredicateAdd={handlePredicateAdd}
          handlePredicateClear={handlePredicateClear}
          handlePredicateRemove={handlePredicateRemove}
          handlePredicateChange={handlePredicateChange}
          handleFilterAdd={handleFilterAdd}
          setPlaybook={setPlaybook}
          onSignalSubmit={onSignalSubmit}
          error={error}
          total={total}
          workspaceSchema={workspaceSchema}
          signal={signal}
          setName={setName}
          setEntityRelation={handleEntityChange}
          setTrigger={setTrigger}
          setSuppressionPeriod={setSuppressionPeriod}
          setModelKey={setModelKey}
          createAction={createAction}
          deleteAction={deleteAction}
          updateAction={updateAction}
          playbooks={playbooks}
          isAutomation={isAutomation}
          setSignal={setSignal}
        />
      </div>
    </Box>
  );
};

export default EditSignalPage;
